<template>
  <basic-card>
    <template slot="header">{{ operationDefinition ? operationDefinition.name : 'N/A' }}
    </template>
    <toolbox>
      <template v-if="this.operation.id">
        <toolbox-button :help-text="def.description" @press="handleExecute(def)"
                        v-for="def in triggerImportDefinitions">
          <icon :icon="['fal', def.icon]" size="lg"/>
        </toolbox-button>
      </template>
    </toolbox>
    <form-wrapper :form-title="$t('operation.meta')">
      <form-text-input caption="Id" v-model="operation.id" disabled/>
      <form-text-input :caption="$t('name')" v-model="operation.name" required/>
      <form-text-input :caption="$t('source')" v-model="operation.source"/>
      <form-select-input v-model="operation.actualStatus" :caption="$t('status')"
                         :options="statusOptions"/>
    </form-wrapper>
    <form-wrapper :form-title="$t('operation.content')">

      <template v-for="parameter in operationDefinition.model.dynaProperties">
        <form-text-input v-if="parameter.details.type === 'STRING'"
                         v-model="operation.content[parameter.name]"
                         :caption="parameter.displayName"
                         :required="parameter.details.required"/>
        <!--                <form-number-input v-if="parameter.details.type === 'DECIMAL'" v-model="operation.content[parameter.name]" :caption="parameter.name"-->
        <!--                                 :required="parameter.details.required"/>-->
        <form-decimal-input v-if="parameter.details.type === 'DECIMAL'"
                            v-model="operation.content[parameter.name]" :caption="parameter.displayName"
                            :precision="parameter.details.precision"
                            :required="parameter.details.required"/>
        <form-number-input v-if="parameter.details.type === 'INTEGER'"
                           v-model="operation.content[parameter.name]" :caption="parameter.displayName"
                           :required="parameter.details.required"/>
        <form-date-input v-if="parameter.details.type === 'DATE'"
                         v-model="operation.content[parameter.name]" :caption="parameter.displayName"
                         :required="parameter.details.required"/>
        <!--                <form-select-input v-if="parameter.type === 'ENTITY'"-->
        <!--                                   v-model="operation.content[parameter.name]" :caption="parameter.name"-->
        <!--                                    :options="[]"/>-->
        <form-checkbox-input v-if="parameter.details.type === 'BOOLEAN'" :caption="parameter.displayName"
                             v-model="operation.content[parameter.name]"/>
        <form-geo-input v-if="parameter.details.type === 'GEO_POINT'" :caption="parameter.displayName"
                        v-model="operation.content[parameter.name]"/>
        <form-select-input
          v-if="parameter.details.type === 'ENTITY_REFERENCE' && parameter.details.entityType == 'currency'"
          v-model="operation.content[parameter.name]" :caption="parameter.displayName"
          :options="currenciesOptions" :custom-renderer="customRenderers.CurrencyVisualizer"/>
        <form-select-input
          v-if="parameter.details.type === 'ENTITY_REFERENCE' && parameter.details.entityType == 'account'"
          v-model="operation.content[parameter.name]" :caption="parameter.displayName"
          :options="accountOptionsByProp[parameter.name]"/>
        <form-select-input
          v-if="parameter.details.type === 'ENTITY_REFERENCE' && parameter.details.entityType == 'party'"
          v-model="operation.content[parameter.name]" :caption="parameter.displayName"
          :options="partyOptions"/>
        <form-select-input
          v-if="parameter.details.type === 'ENTITY_REFERENCE' && parameter.details.entityType == 'operation'"
          v-model="operation.content[parameter.name]" :caption="parameter.displayName" :key="parameter.name"
          :options="operationOptionsByProp[parameter.name]"/>
        <form-select-input v-if="parameter.details.type === 'ENUM'"
                           v-model="operation.content[parameter.name]" :caption="parameter.displayName"
                           :options="parameter.details.enumValues.map(enumVal => ({id: enumVal.value, name: enumVal.name}))"
        />
        <!--                <form-select-input v-if="parameter.type === 'ACCOUNT'"-->
        <!--                                   v-model="operation.parameterValues[parameter.paramName]" :caption="parameter.displayName"-->
        <!--                                    :options="accountOptions(parameter.onlyInternal)"-->
        <!--                />-->
        <!--                <form-select-input v-if="parameter.type === 'PARTY'"-->
        <!--                                   v-model="operation.parameterValues[parameter.paramName]" :caption="parameter.displayName"-->
        <!--                                   :options="partyOptions"-->
        <!--                />-->
      </template>
    </form-wrapper>

    <template v-if="loadingLinkedOptions || loadingLinked">
      <loader indefinite/>
    </template>
    <template v-else>
      <form-wrapper form-title="Linked operations" v-if="secondaryLinkDefinitions.length > 0">
        <template v-for="linkDefinition in secondaryLinkDefinitions">
          <form-select-input
            v-model="operationLinks.primary[linkDefinition.id]" :caption="linkDefinition.primarySideName"
            :key="linkDefinition.primarySideName"
            :options="operationOptionsByLink[linkDefinition.id]"/>
        </template>
      </form-wrapper>


      <framed-content :title="linkDefinition.secondarySideName" v-for="linkDefinition in primaryLinkDefinitions">
        <simple-operations-list v-if="operationDefinitionGetter(linkDefinition.secondaryOperationDefinitions[0])"
                                :operations="operationLinks.secondary[linkDefinition.id]"
                                :link-definition="linkDefinition"
                                :operation-definition="operationDefinitionGetter(linkDefinition.secondaryOperationDefinitions[0])"
                                @create="requestCreate(linkDefinition)"
                                @duplicate="(payload) => requestDuplicate(payload, linkDefinition)"
                                @edit="(payload) => requestEdit(payload, linkDefinition)"
        />
      </framed-content>

    </template>


    <form-wrapper :form-title="$t('operation.audit')">
      <form-select-input v-if="operation.creator != undefined" :caption="$t('creator')"
                         v-model="operation.creator" :options="userOptions"/>
      <form-text-input v-if="operation.creator != undefined" :caption="$t('createdTimestamp')" disabled
                       :value="$formats.formatDateTime(operation.createdTimestamp, false)"/>
      <form-select-input v-if="operation.updatedBy != undefined" :caption="$t('updatedBy')"
                         v-model="operation.updatedBy" :options="userOptions"/>
      <form-text-input v-if="operation.updatedBy != undefined" :caption="$t('updatedTimestamp')" disabled
                       :value="$formats.formatDateTime(operation.updatedTimestamp, false)"/>
      <form-select-input help-item-code="operation-security-group" v-model="operation.securityGroup"
                         :options="securityGroups" :caption="$t('securityGroup')"/>
    </form-wrapper>


    <div
      style="background-color: rgba(200,74,75,0.2); border: 1px dashed rgb(200,74,75); padding: 10px; margin-bottom: 10px;"
      v-if="error">
      <template v-if="error.status == 409">
        Operation you are trying to save is violating unique constraints, currently saved object which is duplicite
        has ID: {{ error.content.currentObjectId }}
      </template>
      <template v-else>
        Error with status {{ error.status }} occured, response content: {{ JSON.stringify(error.content) }}
      </template>
    </div>

    <simple-layout horizontal>
      <simple-button @press="cancel">{{ $t("cancel") }}</simple-button>
      <simple-button @press="save">{{ $t("save") }}</simple-button>
    </simple-layout>
    <v-modal name="createOperationModal" classes="transparent-modal" style="background-color: rgba(10,10,10,0.66)"
             :width="800" :height="600">
      <operation-editor-layout v-if="focusedLinkedOperation != null"
                               :key="focusedLinkedOperation.id"
                               :operation="focusedLinkedOperation"
                               :default-operation-links="primaryLinks"
                               @cancel="cancelFocusedLinkedOperation"
                               @save="saveFocusedLinkedOperation"
                               :name="operation.id"
      />
    </v-modal>


    <v-modal name="loadingModal"
             classes="transparent-modal"
             style="background-color: rgba(10,10,10,0.66)"
             :clickToClose="false">
      <div style="  display: flex;  flex-flow: column; justify-content: center;  align-items: center; height: 100vh;">
        <icon :icon="['fal', 'spinner']" spin size="3x" style="margin-bottom: 16px"/>

        {{ $t("processingData") }}
      </div>

    </v-modal>
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import FormWrapper from "../components/form/FormWrapper";
import FormTextInput from "../components/form/FormTextInput";
import FormSelectInput from "../components/form/FormSelectInput";
import { mapGetters } from "vuex";
import SimpleButton from "../components/button/SimpleButton";
import FormNumberInput from "../components/form/FormNumberInput";
import FormDateInput from "../components/form/FormDateInput";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import FormDecimalInput from "../components/form/FormDecimalInput";
import CollapsibleContent from "../components/CollapsibleContent";
import SimpleLayout from "../components/layout/SimpleLayout";
import FormGeoInput from "../components/form/FormGeoInput";
import ErrorBox from "../components/error/ErrorBox";
import Toolbox from "@/components/toolbox/Toolbox";
import ToolboxButton from "@/components/toolbox/ToolboxButton";
import Axios from "axios";
import FramedContent from "@/components/layout/FramedContent";
import DataTable from "@/components/table/DataTable";
import OperationsListCard from "@/cards/OperationsListCard";
import SimpleOperationsList from "@/components/operations/SimpleOperationsList";
import Loader from "@/components/Loader";
import LoadingView from "@/views/LoadingView";

const TIMEOUT = 60000;
const LIFE_SPAN = 2000;
export default {
  name: "OperationFormCard",
  components: {
    LoadingView,
    OperationEditorLayout: () => import("@/layout/OperationEditorLayout"),
    Loader,
    SimpleOperationsList,
    OperationsListCard,
    DataTable,
    FramedContent,
    ToolboxButton,
    Toolbox,
    ErrorBox,
    FormGeoInput,
    SimpleLayout,
    CollapsibleContent,
    FormDecimalInput,
    FormCheckboxInput,
    FormDateInput,
    FormNumberInput, SimpleButton, FormSelectInput, FormTextInput, FormWrapper, BasicCard
  },
  props: {
    defaultOperationLinks: Object,
    operationDefinition: {
      type: Object,
      default: () => ({
        name: "N/A",
        model: {
          dynaProperties: []
        }
      })
    },
    operation: Object,
    error: Object
  },
  computed: {
    ...mapGetters({
      'currenciesOptions': 'currencies/currenciesOptions',
      'parties': 'parties/parties',
      'users': 'users/users',
      "securityGroups": "securityGroups/securityGroups",
      "importDefinitions": "importDefinitions/importDefinitions",
      "operationDefinitionGetter": "operationDefinitions/operationDefinition",
      "operationLinkDefinitions": "operationLinkDefinitions/operationLinkDefinitions",
      actualPrincipal: "auth/actualPrincipal"
    }),
    actualUser() {
      return this.users.find(usr => usr.username == this.actualPrincipal.username);
    },
    primaryLinkDefinitions() {
      return this.operationLinkDefinitions.filter(def => def.primaryOperationDefinitions.includes(this.operationDefinition.id));
    },
    secondaryLinkDefinitions() {
      return this.operationLinkDefinitions.filter(def => def.secondaryOperationDefinitions.includes(this.operationDefinition.id));
    },
    triggerImportDefinitions() {
      return this.importDefinitions.filter(importDef => importDef.type == "EVENT_TRIGGER"
          && importDef.triggerActionTypes.includes('REQUESTED')
          && (importDef.operationDefinitions ?? []).includes(this.operationDefinition.id))
    },
    primaryLinks() {
      const primary = {};
      this.primaryLinkDefinitions.forEach(link => {
        primary[link.id] = this.operation.id;
      })
      return primary;
    },
    statusOptions() {
      return [
        {id: "DRAFT", name: this.$t('operation.status.draft')},
        {id: "LIVE", name: this.$t('operation.status.live')},
      ]
    },
    partyOptions() {
      return this.parties
        .map(party => ({name: party.displayName, id: party.id}))
    },
    userOptions() {
      return this.users
        .map(user => ({name: user.username, id: user.id}))
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.refreshData();
    })
  },
  data: function () {
    return {
      linkedOperationSaving: false,
      loadingLinked: true,
      loadingLinkedOptions: true,
      accountOptionsByProp: {},
      operationOptionsByProp: {},
      operationOptionsByLink: {},
      creatingOperationForLink: {},
      operationLinks: {
        primary: {},
        secondary: {}
      },
      focusedLinkedOperation: {}
    }
  },
  methods: {
    refreshData() {
      this.getAllLinkedOperations();
      this.loadLinkedOptions();
      Object.keys(this.operationDefinition.model.dynaProperties).map(key => this.operationDefinition.model.dynaProperties[key]).forEach(property => {
        if (property.details.type == 'ENTITY_REFERENCE') {
          if (property.details.entityType == 'account') {
            this.$set(this.accountOptionsByProp, property.name, []);
            this.$store.dispatch("banking/fetchAccounts", {query: property.details.entityFilter})
              .then(data => {
                this.$set(this.accountOptionsByProp, property.name, data)
              });
          } else if (property.details.entityType == 'operation') {
            this.$set(this.operationOptionsByProp, property.name, []);
            this.$store.dispatch("operations/fetchOperations", {
              query: property.details.entityFilter,
              requestedPage: 0,
              size: 10000
            })
              .then(data => {
                this.$set(this.operationOptionsByProp, property.name, data.list.map(datum => ({
                  id: datum.id,
                  name: datum.name ?? datum.id
                })))
              });
          }
        }
      })
    },
    cancelFocusedLinkedOperation() {
      this.$modal.hide("createOperationModal");
      this.focusedLinkedOperation = {};
    },
    saveFocusedLinkedOperation(savePayload) {
      const operation = savePayload.operation;
      const operationLinks = savePayload.links;

      this.linkedOperationSaving = true;
      return this.$store.dispatch(`operations/saveOperation`, operation)
        .then((savedOp) => {
          const linkDefinitions = [this.creatingOperationForLink.id];
          const currentOperationId = savedOp.id;
          return Promise.all(linkDefinitions.map(linkDef => {
            if (!this.operationLinks.secondary[linkDef]) this.operationLinks.secondary[linkDef] = [];
            if (this.operationLinks.secondary[linkDef].map(op => op.id).includes(savedOp.id)) return;
            const targetOperationId = operationLinks.primaryLinks[linkDef];
            return this.$store.dispatch("operationLinks/saveOperationLink", {
              primarySide: targetOperationId,
              secondarySide: currentOperationId,
              linkDefinition: linkDef
            })
          }))
        })
        .then(() => {
          this.linkedOperationSaving = false;
          this.$modal.hide("createOperationModal");
          this.$notify({
            group: 'application',
            title: 'Operation saved',
            text: `Operation ${this.focusedLinkedOperation.id ? this.focusedLinkedOperation.id : ""} saved`,
            type: "ok"
          });
          this.focusedLinkedOperation = {}
          this.operationSaveError = null
        }, (error) => {
          this.operationSaveError = {content: error.response.data, status: error.response.status};
          this.linkedOperationSaving = false
        }).then(this.getAllLinkedOperations);

    },
    requestDuplicate(payload, linkDefinition) {
      this.creatingOperationForLink = linkDefinition;
      this.focusedLinkedOperation = {
        source: 'frontend',
        actualStatus: 'LIVE',
        securityGroup: this.actualUser.defaultSecurityGroup,
        operationDefinition: linkDefinition.secondaryOperationDefinitions[0],
        content: {
          ..._.cloneDeep(payload.content)
        }
      }
      this.$modal.show("createOperationModal");
    },
    requestEdit(payload, linkDefinition) {
      this.creatingOperationForLink = linkDefinition;
      this.focusedLinkedOperation = _.cloneDeep(payload);
      this.$modal.show("createOperationModal");
    },
    requestCreate(linkDefinition) {
      this.creatingOperationForLink = linkDefinition;
      this.focusedLinkedOperation = {
        source: 'frontend',
        actualStatus: 'LIVE',
        securityGroup: this.actualUser.defaultSecurityGroup,
        operationDefinition: linkDefinition.secondaryOperationDefinitions[0],
        content: {}
      }
      this.$modal.show("createOperationModal");
    },
    async getAllLinkedOperations() {
      this.loadingLinked = true;

      const linkedPrimaryOperations = await this.$store.dispatch("operationLinks/fetchOperationLinks", {
        query: {
          parameters: {
            "secondarySide": {
              value: this.operation.id,
              operator: "eq",
              type: "string"
            },
          }
        }
      });
      linkedPrimaryOperations.map(linkedOp => {
        this.operationLinks.primary[linkedOp.linkDefinition] = linkedOp.primarySide;
      })
      Object.keys({...this.defaultOperationLinks}).forEach(linkId => {
        if (!this.operationLinks.primary[linkId]) this.operationLinks.primary[linkId] = this.defaultOperationLinks[linkId];
      })

      const linkedSecondaryOperations = await this.$store.dispatch("operationLinks/fetchOperationLinks", {
        query: {
          parameters: {
            "primarySide": {
              value: this.operation.id,
              operator: "eq",
              type: "string"
            }
          }
        }
      });


      this.operationLinks.secondary = {};
      for (const linkedOp of linkedSecondaryOperations) {
        if (!this.operationLinks.secondary[linkedOp.linkDefinition]) this.operationLinks.secondary[linkedOp.linkDefinition] = [];
        const fetchedOperation = (await this.$store.dispatch("operations/fetchSingleOperation", {id: linkedOp.secondarySide})).data;

        this.operationLinks.secondary[linkedOp.linkDefinition].push(fetchedOperation);
      }


      this.loadingLinked = false;
    },
    async loadLinkedOptions() {
      this.loadingLinkedOptions = true;
      for (const linkDef of this.secondaryLinkDefinitions) {
        const targetDefinitions = linkDef.primaryOperationDefinitions;
        for (const targetDef of targetDefinitions) {
          const options = await this.$store.dispatch("operations/fetchOperations", {
            query: {
              parameters: {
                "operationDefinition": {
                  operator: "eq",
                  type: "string",
                  value: targetDef
                }
              }
            },
            size: 1000,
            requestedPage: 0
          });

          this.operationOptionsByLink[linkDef.id] = options.list.map(op => ({name: op.name ?? op.id, id: op.id}));
        }
      }
      this.loadingLinkedOptions = false;
    },
    async handleExecute(def) {

      this.$modal.show("loadingModal");

      await this.$store.dispatch(`${this.operation.operationDefinition}/saveOperation`, this.operation);

      const response = await Axios.post("processing/api/v1/processes", {
        values: {payload: this.operation, action: "REQUESTED"},
        definitionId: def.id,
      });



      if (response.status === 409) {
        this.$notify({
          group: 'application',
          title: 'Zpracování selhalo',
          text: `Zpracování definice ${def.displayName} selhalo`,
          type: "error"
        });
        return
      }


      const startTime = new Date().getMilliseconds();
      let statusData = {data: {finishTime: null}};
      //TODO: v reportu cashflow v CEPS (EUR_Celek se vetsinou na prvni pokus nepodari prepocet)
      const intervalId = setInterval(async () => {
        if (startTime + TIMEOUT < new Date().getMilliseconds()) {
          clearInterval(intervalId);
          this.$modal.hide("loadingModal");
          this.$notify({
            group: 'application',
            title: `Zpracování nebylo dokončeno`,
            text: `Zpracování ${def.displayName} nebylo dokončeno v časovém limitu.`,
            type: "ok"
          })
        }
        statusData = await Axios.get(response.headers.location);
        if (statusData.data.finishTime) {
          clearInterval(intervalId);
          const refreshedOperation = (await this.$store.dispatch(`${this.operation.operationDefinition}/fetchSingleOperation`, {id: this.operation.id})).data;
          this.$set(this.operation, 'content', refreshedOperation.content)
          this.$modal.hide("loadingModal");

          this.$notify({
            group: 'application',
            title: `Zpracování bylo dokončeno`,
            text: `Zpracování ${def.displayName} proběhlo úspěšně. Operace byla uložena.`,
            type: "ok"
          })
        }
      }, LIFE_SPAN);


    },
    cancel() {
      this.$emit('cancel');
    },
    save() {
      let toSave = this.$_.cloneDeep(this.operation);
      return this.$listeners.save({operation: toSave, links: {primaryLinks: this.operationLinks.primary}});
    },
  }
}
</script>

<style lang="scss" scoped>

</style>
